'use client';

import config from '@actr/config';
import { Sizes } from '@actr/style';
import { Button, ButtonSizes, ButtonTypes } from '@actr/ui/src/components/_atoms/button';
import { FlexDirection, FlexWrapper } from '@actr/ui/src/components/_atoms/flex-wrapper';
import { Text } from '@actr/ui/src/components/_atoms/text';
import { ContentBlock } from '@actr/ui/src/components/content-block';
import { formatMoney } from '@actr/utilities/src/currency';
import React, { memo, useState } from 'react';

import { RouteAirlinesCityToCity } from '@/services/models/avia/route-airlines-city-to-city';
import SeoButton from '@/ui/seo-button';

import * as Styled from './style.css';

type CityAirlinesBlockProps = {
  airlines: RouteAirlinesCityToCity;
  id?: string;
}

const DEFAULT_SHOW_COUNT = 8;

const CityToCityAirlinesBlock: React.FC<CityAirlinesBlockProps> = ({ airlines, id = 'airlines' }) => {
  const [showCount, setShowCount] = useState(DEFAULT_SHOW_COUNT);

  function handleShowMore() {
    setShowCount(prev => prev + DEFAULT_SHOW_COUNT);
  }

  if (!airlines.list.recommendations) {
    return null;
  }

  return (
    <ContentBlock id={id} tagProps={{ itemScope: true, itemType: 'https://schema.org/ItemList' }} className={Styled.ContentBlock}>
      <FlexWrapper gap={Sizes.X2S} direction={FlexDirection.COL}>
        <Text tagName="h3" font="title.x3l" tagProps={{ itemProp: 'name' }}>
          {airlines.title}
        </Text>
        {!!airlines.subtitle && (
          <Text font="paragraph.m" className={Styled.Subtitle}>
            {airlines.subtitle}
          </Text>
        )}
      </FlexWrapper>
      <div className={Styled.AirlinesGrid}>
        {airlines.list.recommendations.map((airline, index) => (
          <div
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
            className={Styled.AirlineWrapper({ isHidden: index >= showCount })}
            key={`${airline.airline_code}-${airline.segments_count}`}>
            <meta itemProp="position" content={(index + 1).toString()} />
            <div itemProp="item" itemScope itemType="https://schema.org/Airline" className={Styled.ItemBlock}>
              <FlexWrapper gap={Sizes.XS} className={Styled.AirlineCard} direction={FlexDirection.COL}>
                <a
                  href={airline.airline_page_url ? `${config.aviakassaURL}/${airline.airline_page_url}` : undefined}
                  className={Styled.Link}>
                  <FlexWrapper gap={Sizes.XS}>
                    <img
                      loading="lazy"
                      itemProp="logo"
                      className={Styled.AirlineLogo}
                      src={`${config.staticURL}/suppliers_redesign/square/${airline.airline_code}.png`}
                      alt={airline.airline_name}
                    />
                    <FlexWrapper gap={Sizes.X4S} direction={FlexDirection.COL}>
                      <Text font="paragraph.xs" className={Styled.Subtitle}>
                        Авиакомпания
                      </Text>
                      <Text font="title.m">
                        {airline.airline_name}
                      </Text>
                      <Text font="paragraph.s">
                        {airline.segments_count === 1 ? 'Прямой рейс' : 'С пересадкой'}
                        {' '}
                        на
                        {' '}
                        {airline.date}
                      </Text>
                    </FlexWrapper>
                  </FlexWrapper>
                </a>
                <SeoButton
                  url={airline.search_url}
                  buttonType={ButtonTypes.SECONDARY}
                  size={ButtonSizes.S}
                  fullWidth
                  className={Styled.SearchLink}>
                  Найти от
                  {' '}
                  {formatMoney(airline.price)}
                </SeoButton>
              </FlexWrapper>
            </div>
          </div>
        ))}
      </div>
      {airlines.list.recommendations.length > showCount && (
        <Button size={ButtonSizes.S} buttonType={ButtonTypes.SECONDARY} onClick={handleShowMore}>
          Показать больше рейсов
        </Button>
      )}
    </ContentBlock>
  );
};

export default memo(CityToCityAirlinesBlock);
